import React, { forwardRef, useImperativeHandle, memo, useState, useEffect, useRef, useCallback } from 'react';
import { Platform, View, StyleSheet } from 'react-native';
import Svg, { Path, Defs, Marker, Polygon } from 'react-native-svg';

const VIEW_BOX = 4000; // Match ActionHierarchy viewport

export interface ActionLinkHandlerHandle {
  setValues: (values: any) => void;
  forceUpdate: () => void;
}

export type ActionLinkHandlerProps = {
  links: any[];
  actionPositionsRef: React.RefObject<Record<string, { x: number, y: number }>>;  // Direct ref access
  theme?: boolean;
  zoomLevel?: number;
};

const ActionLinkHandler = forwardRef<ActionLinkHandlerHandle, ActionLinkHandlerProps>(({
  links,
  actionPositionsRef,  // Direct ref to positions
  theme = true,
  zoomLevel = 1
}, ref) => {
  const componentMountedRef = useRef(true);
  const [updateTrigger, setUpdateTrigger] = useState(0); // Internal update trigger

  console.log('🔗 ActionLinkHandler render:', {
    linksCount: links.length,
    hasPositionsRef: !!actionPositionsRef,
    currentPositionsCount: Object.keys(actionPositionsRef.current || {}).length,
    currentPositions: actionPositionsRef.current
  });

  // Immediate update for real-time tracking - no debounce
  const immediateUpdate = useCallback(() => {
    if (componentMountedRef.current) {
      setUpdateTrigger(prev => prev + 1);
    }
  }, []);

  // Handle imperative updates from parent component
  useImperativeHandle(ref, () => ({
    setValues(values: any) {
      console.log('🎯 ActionLinkHandler setValues (legacy):', values);
    // Legacy method - not needed since we read from ref directly
    },
    forceUpdate() {
      immediateUpdate();
    }
  }));

  // Component cleanup on unmount
  useEffect(() => {
    componentMountedRef.current = true;
    return () => {
      componentMountedRef.current = false;
    };
  }, []);

  if (!links?.length) return null;

  // Don't render until we have positions in the ref
  const currentPositions = actionPositionsRef.current;
  const hasAnyPositions = Object.keys(currentPositions).length > 0;
  if (!hasAnyPositions) {
    console.log('🚫 ActionLinkHandler: No positions available in ref yet, skipping render');
    return null;
  }

  return (
    <Svg
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: -10, // Behind cards
        pointerEvents: 'none' // Allow drag through to underlying components
      }}
    >
      {links.map((link, i) => {
        // Read positions directly from ref - always current!
        // Support both sourceId/targetId and sourceUser/targetUser formats
        const sourceId = link.sourceId || link.sourceUser;
        const targetId = link.targetId || link.targetUser;
        const source = currentPositions[sourceId];
        const target = currentPositions[targetId];

        if (!source || !target) {
          // Only log missing positions once per link to reduce console noise
          const linkKey = `${sourceId}-${targetId}`;
          return null;
        }

        // Calculate connection points (center of cards like ActionHierarchy)
        // Cards are in portrait mode: 320x400
        const sourceX = source.x + 160; // Center of card (320px width / 2)
        const sourceY = source.y + 200; // Center of card (400px height / 2)
        const targetX = target.x + 160;
        const targetY = target.y + 200;

        // Create curved path like original but with modern styling
        const controlX1 = sourceX + (targetX - sourceX) / 2;
        const controlY1 = sourceY + 50;
        const controlX2 = targetX + (sourceX - targetX) / 2;
        const controlY2 = targetY - 50;

        const path = `M ${sourceX} ${sourceY} C ${controlX1} ${controlY1} ${controlX2} ${controlY2} ${targetX} ${targetY}`;

        return (
          <React.Fragment key={`${sourceId}-${targetId}-${i}`}>
            {/* Connection line with arrowhead */}
            <Path
              d={path}
              fill="none"
              stroke={link.color || '#2196F3'}
              strokeWidth={link.width || 3} // Thicker for visibility
              opacity={1.0} // Full opacity for visibility
              strokeLinecap="round"
              markerEnd={`url(#arrowhead-${i}-${updateTrigger})`}
            />
          </React.Fragment>
        );
      })}

      {/* Arrowhead markers */}
      {links.map((link, i) => (
        <Defs key={`defs-${i}-${updateTrigger}`}>
          <marker
            id={`arrowhead-${i}-${updateTrigger}`}
            markerWidth="10"
            markerHeight="7"
            refX="9"
            refY="3.5"
            orient="auto"
          >
            <polygon
              points="0 0, 10 3.5, 0 7"
              fill={link.color || '#2196F3'}
            />
          </marker>
        </Defs>
      ))}
    </Svg>
  );
});

ActionLinkHandler.displayName = 'ActionLinkHandler';

export default memo(ActionLinkHandler, (prevProps, nextProps) => {
  // Only re-render if essential props change (not positions or updateTrigger - those are handled imperatively)
  const linksChanged = JSON.stringify(prevProps.links) !== JSON.stringify(nextProps.links);
  const themeChanged = prevProps.theme !== nextProps.theme;
  const zoomChanged = prevProps.zoomLevel !== nextProps.zoomLevel;

  const shouldRerender = linksChanged || themeChanged || zoomChanged;

  return !shouldRerender; // Return true to prevent re-render
});